// ── La terapia de reemplazo renal como EVENTO del turno ──────────────────────
//
// Dictado del fundador (2026-09-21), sobre su propio trabajo en el team de
// diálisis de Clínica Bupa: son cinco, están a cargo de las terapias
// extracorpóreas que salgan en UCI y coronaria, y cada vez que sale una
// diálisis el mismo hecho se escribe CUATRO veces: al grupo de WhatsApp donde
// están los nefrólogos, al bloc de notas del teléfono para no olvidarlo, al
// formulario donde va el episodio, y a la evolución de SAP. "A mí al menos se
// me olvida siempre hacerlo, es súper tedioso."
//
// El bloc de notas es el síntoma: existe solo porque el formulario no está a
// mano en el momento en que el dato está fresco, y mientras exista un "después"
// va a haber olvido. Así que esto no agrega un recordatorio (sería la culpa que
// la casa no usa): mueve el registro al momento de conectar, y de esa única
// captura salen las tres salidas (ver `reporteTerapia.js`).
//
// El VOCABULARIO ya existía entero en la evolución por sistema (rama `trr` de
// renal en `escalasEvolucion.js`, ola del 2026-07-16): modalidad, Qb, Qd, Qs,
// tasa de UF, tolerancia, filtro, catéter y anticoagulación del circuito. Lo
// que faltaba era el EVENTO. Por eso acá NO se escribe un segundo catálogo:
// todo se DERIVA de `CAMPOS_POR_SISTEMA.renal`, incluida la prosa de los
// números (`proseFmt`), y un test lo fija. Misma regla con que `pielLpp` es la
// fuente de las zonas de curación: un catálogo, dos superficies.
import { CAMPOS_POR_SISTEMA } from './escalasEvolucion';

// Def de un campo de la evolución renal. Perezoso a propósito: `escalasEvolucion`
// solo importa `pielLpp`, así que no hay ciclo, pero leer el catálogo dentro de
// la función deja el módulo inmune a un import futuro que sí lo cree.
const defRenal = (k) => (CAMPOS_POR_SISTEMA.renal || []).find((c) => c.k === k) || null;
const opcionesRenal = (k) => defRenal(k)?.opciones || [];

// ── Qué terapias conecta el team ─────────────────────────────────────────────
// SOLO las dos extracorpóreas. La peritoneodiálisis existe en el catálogo de la
// evolución y se queda fuera de esta acción a propósito: no se conecta a un
// circuito, y ofrecerla acá afirmaría que este evento la cubre.
// `valor` es LITERAL el string del catálogo de la evolución (fijado por test):
// si mañana alguien renombra una opción allá, el test cae acá.
export const TRR_TERAPIAS = [
  { id: 'trrc', valor: 'Terapia continua (TRRC)',  label: 'Terapia continua', sujeto: null },
  { id: 'hdi',  valor: 'Hemodiálisis intermitente', label: 'Hemodiálisis intermitente', sujeto: 'hemodiálisis intermitente' },
];
export const terapiaTrrPorId = (id) => TRR_TERAPIAS.find((t) => t.id === id) || null;

export const TRR_MODALIDADES  = () => opcionesRenal('trrcModalidad');
export const TRR_ANTICOAG     = () => opcionesRenal('trrAnticoag');
export const TRR_ACCESOS      = () => opcionesRenal('trrAcceso');
export const TRR_TOLERANCIAS  = () => opcionesRenal('trrcTolerancia');
export const TRR_FILTROS      = () => opcionesRenal('crrtFiltro');

// Los NÚMEROS de la conexión, por terapia. La clave de la acción se mapea al
// campo de la evolución y de ahí salen el label, la unidad, la pista didáctica
// y la PROSA (`proseFmt`), que es lo que de verdad importa: la cronología y el
// documento imprimen el número con la misma redacción, imposible que se
// desalineen.
const NUMEROS_POR_TERAPIA = {
  trrc: [
    { k: 'qb',     campo: 'trrcQb' },
    { k: 'qd',     campo: 'trrcQd' },
    { k: 'qs',     campo: 'trrcQs' },
    { k: 'ufTasa', campo: 'trrcTasaUf' },
  ],
  hdi: [
    { k: 'qb',         campo: 'hdiQb' },
    { k: 'ufObjetivo', campo: 'hdiUfObjetivo' },
  ],
};

// Campos numéricos de una terapia, ya resueltos contra el catálogo de la
// evolución. La UI los recorre para dibujar los inputs; la prosa, para
// imprimirlos. Una sola lista, un solo orden.
export function numerosTerapia(terapiaId) {
  return (NUMEROS_POR_TERAPIA[terapiaId] || []).map(({ k, campo }) => {
    const def = defRenal(campo) || {};
    return {
      k,
      campo,
      label: def.label || k,
      unidad: def.unidad || '',
      pista: def.pista || '',
      proseFmt: def.proseFmt || `${def.label || k} {v}${def.unidad ? ` ${def.unidad}` : ''}`,
    };
  });
}

// ── Por qué se desconecta ────────────────────────────────────────────────────
// Catálogo propio (la evolución no lo tiene: allá la terapia es un ESTADO, acá
// es un hecho con su hora). `termina` distingue la desconexión que cierra la
// terapia de la que es una pausa: un cambio de set o un traslado NO permiten
// afirmar "sin terapia de reemplazo renal", que sería justo el plan promovido a
// hecho que la casa prohíbe. Se reconecta y nadie se lo dijo al documento.
export const TRR_DESCONEXION_MOTIVOS = [
  { label: 'Fin de la indicación',      termina: true },
  { label: 'Coagulación del filtro',    termina: false },
  { label: 'Cambio de set',             termina: false },
  { label: 'Traslado del paciente',     termina: false },
  { label: 'Mala tolerancia',           termina: false },
  { label: 'Retiro del catéter',        termina: true },
];
export const motivoDesconexion = (label) => TRR_DESCONEXION_MOTIVOS.find((m) => m.label === label) || null;

// ── Números: re-validados AL LEER ────────────────────────────────────────────
// Doctrina MEDIDA_RE de `pielLpp`: un jsonb adulterado o editado a mano jamás
// imprime un valor. Y el punto de MILES chileno ("1.500" son mil quinientos, no
// uno coma cinco) se saca antes de validar, solo con el patrón exacto de miles,
// así que "1.5" se sigue leyendo como decimal.
const NUM_RE = /^\d{1,5}([,.]\d{1,2})?$/;
const MILES_RE = /^\d{1,3}(\.\d{3})+$/;
export function numeroTrr(v) {
  let t = String(v ?? '').trim();
  if (!t) return null;
  if (MILES_RE.test(t)) t = t.replace(/\./g, '');
  if (!NUM_RE.test(t)) return null;
  // Decimal con coma, que es como se escribe acá; el separador de miles no se
  // re-agrega a propósito: la Mesa imprime el número tal cual se tipeó y dos
  // formatos distintos para el mismo dato es justo la incoherencia que la casa
  // persigue.
  return t.replace('.', ',');
}

const mayuscula = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// "a, b y c". Tres líneas duplicadas antes que un import que crearía un ciclo
// con registroTurno (mismo criterio que `listaProsaAcciones` allá).
function listaProsa(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

// ── Lo mínimo para registrar ─────────────────────────────────────────────────
// Mismo criterio que la intubación ("la TÉCNICA es lo mínimo"): lo que dice QUÉ
// pasó. Para una continua eso incluye la modalidad, porque "TRRC" sin modalidad
// no le sirve al nefrólogo que la indicó: es el dato que dice qué hace la
// máquina. Los flujos enriquecen y no bloquean (captura ahora, estructura
// después).
export function conexionTrrCompleta(valores = {}) {
  const t = terapiaTrrPorId(valores.terapia);
  if (!t) return false;
  if (t.id === 'trrc') return TRR_MODALIDADES().includes(valores.modalidad);
  return true;
}
export function desconexionTrrCompleta(valores = {}) {
  return !!motivoDesconexion(valores.motivo);
}

// El sujeto de la frase: para una continua, la modalidad ES el nombre de lo que
// se conectó (HDFVVC dice más que "terapia continua"); para la intermitente, la
// terapia misma.
export function sujetoTerapia(valores = {}) {
  const t = terapiaTrrPorId(valores.terapia);
  if (!t) return null;
  if (t.id === 'trrc') return TRR_MODALIDADES().includes(valores.modalidad) ? valores.modalidad : null;
  return t.sujeto;
}

// Los números que la conexión trae, ya en prosa ("Qb 150 ml/min").
export function numerosTerapiaProsa(valores = {}) {
  return numerosTerapia(valores.terapia)
    .map(({ k, proseFmt }) => {
      const n = numeroTrr(valores[k]);
      return n ? proseFmt.replace('{v}', n) : null;
    })
    .filter(Boolean);
}

// LA FRASE DE LA CONEXIÓN, fuente única para la cronología y para la evolución.
// Sin hora: la norma del fundador (2026-08-02) es que la prosa generada no la
// imprime, el contexto ancla y el sello vive en los DATOS de la entrada. En el
// mensaje al grupo la hora sí va, y va como CAMPO, no como prosa (ver
// `reporteTerapia.js`).
export function conexionTrrProsa(valores = {}) {
  if (!conexionTrrCompleta(valores)) return '';
  const sujeto = sujetoTerapia(valores);
  const nums = numerosTerapiaProsa(valores);
  const frases = [`Se conecta a ${sujeto}${nums.length ? `, con ${listaProsa(nums)}` : ''}.`];
  if (TRR_ANTICOAG().includes(valores.anticoag)) frases.push(`${mayuscula(valores.anticoag)}.`);
  if (TRR_ACCESOS().includes(valores.acceso)) {
    // La prosa del acceso sale del MISMO `prose` de la evolución ("catéter de
    // diálisis funcionando lineal"): la opción suelta ("Funcionando lineal") no
    // dice funcionando qué.
    const prose = defRenal('trrAcceso')?.prose?.[valores.acceso];
    if (prose) frases.push(`${mayuscula(prose)}.`);
  }
  return frases.join(' ');
}

// "Bien tolerada" sola no dice tolerada QUÉ (la evolución tiene el mismo
// problema y lo resuelve con su `prose`: "TRRC bien tolerada"). Acá no se puede
// reusar ese mapa: la desconexión no declara la terapia, y una HD intermitente
// desconectada no puede salir impresa como TRRC. Así que el sujeto es genérico
// y verdadero para las dos. Las demás opciones ya son autocontenidas.
const TOLERANCIA_PROSA = {
  'Bien tolerada': 'Terapia bien tolerada',
  'Mal tolerada': 'Terapia mal tolerada',
};

export function desconexionTrrProsa(valores = {}) {
  if (!desconexionTrrCompleta(valores)) return '';
  const horas = numeroTrr(valores.horasEfectivas);
  const uf = numeroTrr(valores.ufTotal);
  const detalle = [
    horas ? `${horas} horas efectivas de terapia` : null,
    uf ? `UF total ${uf} ml` : null,
  ].filter(Boolean);
  const frases = [`Se desconecta la terapia por ${valores.motivo.toLowerCase()}${detalle.length ? `, con ${listaProsa(detalle)}` : ''}.`];
  if (TRR_TOLERANCIAS().includes(valores.tolerancia)) frases.push(`${mayuscula(TOLERANCIA_PROSA[valores.tolerancia] || valores.tolerancia)}.`);
  if (TRR_FILTROS().includes(valores.filtro)) frases.push(`${mayuscula(valores.filtro)}.`);
  return frases.join(' ');
}

// Cómo QUEDA tras desconectar, y solo cuando se puede afirmar. Un cambio de set
// o un traslado son pausas: decir "sin terapia de reemplazo renal" sería
// declarar terminado algo que se va a reconectar.
export function estadoTrasDesconexion(valores = {}) {
  if (!desconexionTrrCompleta(valores)) return null;
  return motivoDesconexion(valores.motivo)?.termina ? 'sin terapia de reemplazo renal' : null;
}
